import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import moment from 'moment';
import {
  Box,
  Divider,
  Typography,
  Grid,
  Radio,
  FormControlLabel,
  FormHelperText,
} from '@mui/material';
import * as Yup from 'yup';
import { useFormik } from 'formik';

import {
  FBoxContainer,
  FCheckbox,
  FHAntdDateTimePicker,
  FHAutocomplete,
  FHButton,
  FHInputField,
  FHRadioGroup,
  FHSelect,
  FProgressBar,
} from '../../components/base';
import { clientActions, payorActions } from '../../store';
import {
  cleanNumericInputValue,
  disableAllDatesExceptLastOfMonth,
  disabledFutureDate,
  history,
} from '../../helpers';
import PageContainer from '../../components/layout/PageContainer';
import { STANDARD_ERROR_MESSAGES, BackgroundTypes } from '../../constants';
import { RelationshipToPolicyholder } from '../constants/general';

import type { RootState } from '../../store';
import type { SelectChangeEvent } from '@mui/material';
import type { Moment } from 'moment';
import type { ChangeEvent } from 'react';
import type { IPayor, IOption, IClientPolicyMap, SelectValueType } from '../../utils/types';

const { required, tenDigits, twoChars, minFiveDigits } = STANDARD_ERROR_MESSAGES;

const AssignPayorValidationSchema = Yup.object().shape({
  client_id: Yup.string().trim().required(required),
  payor_id: Yup.string().trim().required(required),
  billing_payor_id: Yup.string().trim().required(required),
  date_start: Yup.string().trim().required(),
  date_end: Yup.string().trim().optional().nullable(),
  member_id: Yup.string().trim().required(),
  payor_responsibility: Yup.string().trim().required(),
  is_policy_holder_an_existing_provider: Yup.boolean().optional().nullable().notRequired(),
  relationship_to_policyholder: Yup.string().trim().required(),
  is_monthly_plan: Yup.boolean().optional().nullable().notRequired(),
  group_number: Yup.string().optional().notRequired(),
  name: Yup.string().trim().required(),
  sex: Yup.string().trim().required(),
  dob: Yup.string().trim().required(),
  phone: Yup.string().trim().min(10, tenDigits).max(10, tenDigits).required(required),
  street_address_line_one: Yup.string().trim().required(),
  street_address_line_two: Yup.string().trim().optional().notRequired(),
  street_address_state: Yup.string()
    .trim()
    .max(2, 'Max 2 Chars are allowed')
    .min(2, 'Min 2 Chars are required')
    .required(required),
  street_address_city: Yup.string().trim().required(),
  street_address_zip: Yup.string().trim().required().min(5, 'Min 5 Digits are required'),
  is_PA1_required: Yup.boolean().optional().nullable().required(),
  is_PA2_required: Yup.boolean().optional().nullable().required(),
});

const InitialAssignProviderValues = {
  client_id: '',
  payor_id: '',
  billing_payor_id: '',
  member_id: '',
  date_start: null,
  date_end: null,
  relationship_to_policyholder: '',
  is_policy_holder_an_existing_provider: false,
  payor_responsibility: '',
  group_number: null,
  name: '',
  sex: '',
  is_monthly_plan: null,
  dob: null,
  phone: '',
  street_address_line_one: '',
  street_address_line_two: '',
  street_address_state: '',
  street_address_city: '',
  street_address_zip: '',
  is_PA1_required: true,
  is_PA2_required: true,
  create_eb_tracker_entry: 'yes',
};

function ClientPolicyForm() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const {
    selected: currentClient,
    loading: clientLoading,
    selectedClientPlans = [],
  } = useSelector((state: any) => state.clients);
  const { list: payors = [], loading: payorsLoading } = useSelector(
    (state: RootState) => state.payors,
  );
  const [selectedClient, setSelectedClient] = useState<IOption | null>(null);
  const [selectedPayor, setSelectedPayor] = useState<IOption | null>(null);
  const [selectedBillingPayor, setSelectedBillingPayor] = useState<IOption | null>(null);
  const [billingPayorIsSameAsPayor, setBillingPayorIsSameAsPayor] = useState<boolean>(false);
  const [isMonthlyPlan, setIsMonthlyPlan] = useState<boolean | null>(null);

  const { clientId, policyId } = useParams();

  useEffect(() => {
    if (!clientId || !currentClient) return;
    setSelectedClient({
      id: currentClient.id,
      label: `${currentClient.first_name} ${currentClient.last_name}`,
    });
  }, [clientId, currentClient /*, clients*/]);

  useEffect(() => {
    dispatch(clientActions.getClient({ getAccessTokenSilently, clientId }));
    dispatch(payorActions.getList({ getAccessTokenSilently }));
    if (policyId) {
      dispatch(clientActions.getClientSpecificPayorsMapping({ getAccessTokenSilently, clientId }));
    }
  }, [clientId, policyId]);

  useEffect(() => {
    if (!clientId || !policyId) return;
    const selectedPolicy = selectedClientPlans.find(
      (policy: IClientPolicyMap) => policy.id === parseInt(policyId),
    );
    if (!selectedPolicy) return;
    const { id: _id, payor = {}, billing_payor = {}, policyholder = {}, ...rest } = selectedPolicy;
    setValues({
      ...values,
      ...rest,
      ...policyholder,
      payor_id: payor?.id,
      billing_payor_id: billing_payor?.id,
    });
    setSelectedPayor({
      id: payor?.id,
      label: `${payor?.label}, ${payor?.state?.toUpperCase()}, id ${payor?.id}, chpid ${
        payor?.clearinghouse_payor_id
      }`,
    });
    setIsMonthlyPlan(rest.is_monthly_plan);
    setSelectedBillingPayor({
      id: billing_payor?.id,
      label: `${billing_payor?.label}, ${billing_payor?.state?.toUpperCase()}, id ${
        billing_payor?.id
      }, chpid ${billing_payor?.clearinghouse_payor_id}`,
    });
    setBillingPayorIsSameAsPayor(
      Boolean(payor?.id) && Boolean(billing_payor?.id) && payor.id === billing_payor.id,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policyId, selectedClientPlans]);

  const formik = useFormik({
    initialValues: clientId
      ? { ...InitialAssignProviderValues, client_id: clientId }
      : InitialAssignProviderValues,
    onSubmit: async (values: any) => {
      let response: any = null;
      if (policyId) {
        response = await dispatch(
          clientActions.editPolicy({
            data: {
              payload: {
                date_start: moment(values['date_start']).format('YYYY-MM-DD'),
                date_end: values['date_end']
                  ? (moment(values['date_end'])?.format('YYYY-MM-DD') ?? null)
                  : null,
                payor_responsibility: values['payor_responsibility'],
                payor_id: values['payor_id'],
                billing_payor_id: values['billing_payor_id'],
                member_id: values['member_id'],
                relationship_to_policyholder: values['relationship_to_policyholder'],
                group_number: values['group_number'],
                is_monthly_plan: values['is_monthly_plan'],
                policyholder: {
                  name: values['name']?.trim(),
                  sex: values['sex'],
                  dob: values['dob'],
                  phone: values['phone'],
                  street_address_line_one: values['street_address_line_one']?.trim(),
                  street_address_line_two: values['street_address_line_two']?.trim(),
                  street_address_city: values['street_address_city']?.trim(),
                  street_address_state: values['street_address_state']?.trim(),
                  street_address_zip: values['street_address_zip']?.trim(),
                },
                is_PA1_required: values['is_PA1_required'],
                is_PA2_required: values['is_PA2_required'],
              },
            },
            getAccessTokenSilently,
            policyId,
          }),
        );
      } else {
        response = await dispatch(
          clientActions.assignPayor({
            data: {
              payload: {
                payor_id: values['payor_id'],
                billing_payor_id: values['billing_payor_id'],
                client_id: values['client_id'],
                date_start: moment(values['date_start']).format('YYYY-MM-DD'),
                date_end: values['date_end']
                  ? (moment(values['date_end'])?.format('YYYY-MM-DD') ?? null)
                  : null,
                payor_responsibility: values['payor_responsibility'],
                member_id: values['member_id'],
                relationship_to_policyholder: values['relationship_to_policyholder'],
                group_number: values['group_number'],
                policyholder: {
                  name: values['name'],
                  sex: values['sex'],
                  dob: values['dob'],
                  phone: values['phone'],
                  street_address_line_one: values['street_address_line_one'],
                  street_address_line_two: values['street_address_line_two'],
                  street_address_city: values['street_address_city'],
                  street_address_state: values['street_address_state'],
                  street_address_zip: values['street_address_zip'],
                },
                is_PA1_required: values['is_PA1_required'],
                is_PA2_required: values['is_PA2_required'],
                is_monthly_plan: values['is_monthly_plan'],
                create_eb_tracker_entry: values['create_eb_tracker_entry'] === 'yes' ? true : false,
              },
            },
            getAccessTokenSilently,
          }),
        );
      }

      if (!response.error) {
        if (!policyId) {
          history.navigate(`/admin/edit-policy/${clientId}/${response.payload.id}`);
        }
      }
    },
    validationSchema: AssignPayorValidationSchema,
  });

  const { setFieldValue, submitForm, touched, errors, values, setValues } = formik;
  const handleUpdateFieldValues = (key: string, value: any) => {
    let updatedValue = value;
    if (key === 'phone') {
      updatedValue = cleanNumericInputValue(value.trim());
    }
    setFieldValue(key, updatedValue);
  };

  return (
    <PageContainer>
      {(clientLoading || payorsLoading) && <FProgressBar />}
      <Box sx={{ mt: 3 }}>
        <FBoxContainer sx={{ borderRadius: '20px' }}>
          <form
            data-testid="clientPolicyForm"
            onSubmit={formik.handleSubmit}
            style={{ width: '100%' }}
          >
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Box ml={1} sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                    <Typography textAlign={'left'} variant="h4">
                      {policyId ? 'Edit Policy' : 'Create Policy'}
                    </Typography>
                  </Box>
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={4}>
                      <Typography textAlign={'left'}>Client:</Typography>
                    </Grid>
                    <Grid item xs={7.5}>
                      <FHAutocomplete
                        inputHeight={40}
                        fullWidth
                        placeholder="Select Client"
                        isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                        disabled={Boolean(clientId)}
                        options={[selectedClient]}
                        renderOption={(props: any, option: IOption) => {
                          return (
                            <li {...props} key={option.id}>
                              {option.label}
                            </li>
                          );
                        }}
                        value={selectedClient}
                        onChange={(
                          event: ChangeEvent<HTMLSelectElement>,
                          newValue: IOption | null,
                        ) => {
                          setSelectedClient(newValue);
                          handleUpdateFieldValues('client_id', newValue?.id);
                        }}
                        error={touched.client_id && errors.client_id}
                        helperText={touched.client_id && errors.client_id && required}
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={4}>
                      <Typography textAlign={'left'}>Payor:</Typography>
                    </Grid>
                    <Grid item xs={7.5}>
                      <FHAutocomplete
                        data-testid="clientPolicySelect"
                        inputHeight={40}
                        fullWidth
                        disabled={Boolean(policyId)}
                        placeholder="Select Payor"
                        isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                        options={payors.map((payor: IPayor) => ({
                          id: payor.id,
                          label: `${payor?.label}, ${payor?.state?.toUpperCase()}, id ${
                            payor?.id
                          }, chpid ${payor?.clearinghouse_payor_id}`,
                        }))}
                        value={selectedPayor}
                        onChange={async (
                          event: ChangeEvent<HTMLSelectElement>,
                          newValue: IOption | null,
                        ) => {
                          setSelectedPayor(newValue);
                          await handleUpdateFieldValues('payor_id', newValue?.id ?? '');
                          if (billingPayorIsSameAsPayor) {
                            setSelectedBillingPayor(newValue);
                            await handleUpdateFieldValues('billing_payor_id', newValue?.id ?? '');
                          }
                        }}
                        error={touched.payor_id && errors.payor_id}
                        helperText={touched.payor_id && errors.payor_id && required}
                      />
                    </Grid>
                  </Grid>

                  <Grid container p={1} spacing={1} direction="row" alignItems="flex-end">
                    <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                      <FCheckbox
                        data-testid="isBillingPayorSameAsPayorCheckbox"
                        label="Billing payor is same as Payor"
                        checked={billingPayorIsSameAsPayor}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          const isChecked = e.target.checked;
                          setBillingPayorIsSameAsPayor(isChecked);

                          // Update billing payor based on checkbox state
                          if (isChecked && selectedPayor) {
                            setSelectedBillingPayor(selectedPayor);
                            handleUpdateFieldValues('billing_payor_id', selectedPayor.id);
                          } else {
                            // Clear billing payor when unchecked
                            setSelectedBillingPayor(null);
                            handleUpdateFieldValues('billing_payor_id', null);
                          }
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={4}>
                      <Typography textAlign={'left'}>Billing payor:</Typography>
                    </Grid>
                    <Grid item xs={7.5}>
                      <FHAutocomplete
                        data-testid="clientPolicyBillingSelect"
                        inputHeight={40}
                        fullWidth
                        placeholder="Select billing payor"
                        isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                        options={payors.map((payor: IPayor) => ({
                          id: payor.id,
                          label: `${payor?.label}, ${payor?.state?.toUpperCase()}, id ${
                            payor?.id
                          }, chpid ${payor?.clearinghouse_payor_id}`,
                        }))}
                        value={selectedBillingPayor}
                        onChange={(
                          event: ChangeEvent<HTMLSelectElement>,
                          newValue: IOption | null,
                        ) => {
                          setSelectedBillingPayor(newValue);
                          const payorId = newValue?.id ?? '';
                          handleUpdateFieldValues('billing_payor_id', payorId);

                          // Only set as same if both have valid IDs and they match
                          setBillingPayorIsSameAsPayor(
                            Boolean(newValue?.id) &&
                              Boolean(selectedPayor?.id) &&
                              newValue?.id === selectedPayor?.id,
                          );
                        }}
                        error={touched.billing_payor_id && errors.billing_payor_id}
                        helperText={touched.billing_payor_id && errors.billing_payor_id && required}
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={4}>
                      <Typography textAlign={'left'}>Date start:</Typography>{' '}
                    </Grid>
                    <Grid item xs={7.5}>
                      <FHAntdDateTimePicker
                        data-testid="clientPolicyDate"
                        format="MM/DD/YYYY"
                        inputHeight={40}
                        style={{ width: '100%' }}
                        error={touched.date_start && Boolean(errors.date_start)}
                        helperText={touched.date_start && required}
                        allowClear={false}
                        value={values['date_start'] ? moment(values['date_start']) : null}
                        onSelect={(value: Moment) =>
                          handleUpdateFieldValues('date_start', value.format('YYYY-MM-DD'))
                        }
                        onChange={(value: Moment) =>
                          handleUpdateFieldValues('date_start', value.format('YYYY-MM-DD'))
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={4}>
                      <Typography textAlign={'left'}>Date end:</Typography>{' '}
                    </Grid>
                    <Grid item xs={7.5}>
                      <FHAntdDateTimePicker
                        data-testid="clientPolicyEndDate"
                        format="MM/DD/YYYY"
                        inputHeight={40}
                        style={{ width: '100%' }}
                        error={touched.date_end && Boolean(errors.date_end)}
                        helperText={touched.date_end && required}
                        allowClear={true}
                        value={values['date_end'] ? moment(values['date_end']) : null}
                        disabledDate={disableAllDatesExceptLastOfMonth}
                        onSelect={(value: Moment) =>
                          handleUpdateFieldValues(
                            'date_end',
                            value ? value.format('YYYY-MM-DD') : null,
                          )
                        }
                        onChange={(value: Moment) =>
                          handleUpdateFieldValues(
                            'date_end',
                            value ? value.format('YYYY-MM-DD') : null,
                          )
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={4}>
                      <Typography textAlign={'left'}>Member Id:</Typography>
                    </Grid>
                    <Grid item xs={7.5}>
                      <FHInputField
                        inputProps={{ 'data-testid': 'clientPolicyMember' }}
                        inputHeight={40}
                        value={values['member_id']}
                        fullWidth
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('member_id', e.target.value)
                        }
                        error={touched.member_id && Boolean(errors.member_id)}
                        helperText={touched.member_id && errors.member_id && required}
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={4}>
                      <Typography textAlign={'left'}>Relationship to policyholder:</Typography>
                    </Grid>
                    <Grid item xs={7.5}>
                      <FHSelect
                        data-testid="clientPolicyRelationShip"
                        fullWidth
                        inputHeight={40}
                        backgroundType={BackgroundTypes.White}
                        options={Object.values(RelationshipToPolicyholder).map((value) => ({
                          value,
                          label: value,
                        }))}
                        value={values['relationship_to_policyholder']}
                        onUpdate={(value: SelectValueType) => {
                          handleUpdateFieldValues('relationship_to_policyholder', value);
                        }}
                        error={
                          touched.relationship_to_policyholder &&
                          Boolean(errors.relationship_to_policyholder)
                        }
                        helperText={
                          touched.relationship_to_policyholder &&
                          errors.relationship_to_policyholder &&
                          required
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={4}>
                      <Typography textAlign={'left'}>Payor responsibility:</Typography>
                    </Grid>
                    <Grid item xs={7.5}>
                      <FHSelect
                        data-testid="clientPolicyResponsibility"
                        fullWidth
                        inputHeight={40}
                        backgroundType={BackgroundTypes.White}
                        options={[
                          { value: 'PRIMARY', label: 'Primary' },
                          { value: 'SECONDARY', label: 'Secondary' },
                          { value: 'TERTIARY', label: 'Tertiary' },
                        ]}
                        value={values['payor_responsibility']}
                        onUpdate={(value: SelectValueType) => {
                          handleUpdateFieldValues('payor_responsibility', value);
                        }}
                        error={touched.payor_responsibility && Boolean(errors.payor_responsibility)}
                        helperText={
                          touched.payor_responsibility && errors.payor_responsibility && required
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={4}>
                      <Typography textAlign={'left'}>Group number:</Typography>
                    </Grid>
                    <Grid item xs={7.5}>
                      <FHInputField
                        inputProps={{ 'data-testid': 'clientPolicyGroup' }}
                        inputHeight={40}
                        value={values['group_number']}
                        fullWidth
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues(
                            'group_number',
                            e.target.value ? e.target.value : null,
                          )
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={4}>
                      <Typography textAlign={'left'}>Is PA 1 required?</Typography>
                    </Grid>
                    <Grid item xs={7.5}>
                      <FHRadioGroup
                        row
                        value={values['is_PA1_required']}
                        sx={{
                          justifyContent: 'flex-start',
                          '& .MuiFormControlLabel-root': {
                            marginRight: 4,
                          },
                        }}
                        name="existing-provider-radio-buttons-group"
                        onChange={(e: SelectChangeEvent<unknown>) => {
                          handleUpdateFieldValues(
                            'is_PA1_required',
                            e.target.value === 'true' ? true : false,
                          );
                        }}
                      >
                        <FormControlLabel
                          value={true}
                          control={<Radio />}
                          data-testid="isPa1RequiredYes"
                          label="Yes"
                        />
                        <FormControlLabel
                          value={false}
                          control={<Radio />}
                          data-testid="isPa1RequiredNo"
                          label="No"
                        />
                      </FHRadioGroup>

                      {touched.is_PA1_required && Boolean(errors.is_PA1_required) && (
                        <FormHelperText sx={{ color: 'red', fontSize: '0.75rem' }}>
                          {'Select whether PA 1 is required or not'}
                        </FormHelperText>
                      )}
                    </Grid>
                  </Grid>
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={4}>
                      <Typography textAlign={'left'}>Is PA 2 required?</Typography>
                    </Grid>
                    <Grid item xs={7.5}>
                      <FHRadioGroup
                        row
                        value={values['is_PA2_required']}
                        sx={{
                          justifyContent: 'flex-start',
                          '& .MuiFormControlLabel-root': {
                            marginRight: 4,
                          },
                        }}
                        name="existing-provider-radio-buttons-group"
                        onChange={(e: SelectChangeEvent<unknown>) => {
                          handleUpdateFieldValues(
                            'is_PA2_required',
                            e.target.value === 'true' ? true : false,
                          );
                        }}
                      >
                        <FormControlLabel
                          value={true}
                          control={<Radio />}
                          data-testid="isPa2RequiredYes"
                          label="Yes"
                        />
                        <FormControlLabel
                          value={false}
                          control={<Radio />}
                          data-testid="isPa2RequiredNo"
                          label="No"
                        />
                      </FHRadioGroup>
                      {touched.is_PA2_required && Boolean(errors.is_PA2_required) && (
                        <FormHelperText sx={{ color: 'red', fontSize: '0.75rem' }}>
                          {'Select whether PA 2 is required or not'}
                        </FormHelperText>
                      )}
                    </Grid>
                  </Grid>
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={4}>
                      <Typography textAlign={'left'}>Is monthly plan?</Typography>
                    </Grid>
                    <Grid item xs={7.5}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <FCheckbox
                          data-testid="isMonthlyPlanCheckbox"
                          label="Is monthly plan"
                          checked={isMonthlyPlan ?? false}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            const isChecked = e.target.checked;
                            setIsMonthlyPlan(isChecked);
                            handleUpdateFieldValues('is_monthly_plan', isChecked);
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ ml: 3, mt: 3, display: { xs: 'none', md: 'block' } }}
              />
              <Grid item xs={12}>
                <Typography fontWeight={'bold'} textAlign={'left'} my={0.5} p={1}>
                  Policyholder Info:
                </Typography>
                <Grid container p={1} spacing={1} direction="row" alignItems="center">
                  <Grid item xs={4}>
                    <Typography textAlign={'left'}>Name:</Typography>
                  </Grid>
                  <Grid item xs={7.5}>
                    <FHInputField
                      inputProps={{ 'data-testid': 'clientPolicyholderName' }}
                      fullWidth
                      inputHeight={40}
                      value={values['name']}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handleUpdateFieldValues('name', e.target.value)
                      }
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name && required}
                    />
                  </Grid>
                </Grid>
                <Grid container p={1} spacing={1} direction="row" alignItems="center">
                  <Grid item xs={4}>
                    <Typography textAlign={'left'}>DOB:</Typography>{' '}
                  </Grid>
                  <Grid item xs={7.5}>
                    <FHAntdDateTimePicker
                      data-testid="clientPolicyholderDOB"
                      format="MM/DD/YYYY"
                      inputHeight={40}
                      style={{ width: '100%' }}
                      disabledDate={disabledFutureDate}
                      error={touched.dob && Boolean(errors.dob)}
                      helperText={touched.dob && required}
                      allowClear={false}
                      value={values['dob'] ? moment(values['dob']) : null}
                      onSelect={(value: Moment) =>
                        handleUpdateFieldValues('dob', value.format('YYYY-MM-DD'))
                      }
                      onChange={(value: Moment) =>
                        handleUpdateFieldValues('dob', value.format('YYYY-MM-DD'))
                      }
                    />
                  </Grid>
                </Grid>
                <Grid container p={1} spacing={1} direction="row" alignItems="center">
                  <Grid item xs={4}>
                    <Typography textAlign={'left'}>Phone:</Typography>
                  </Grid>
                  <Grid item xs={7.5}>
                    <FHInputField
                      inputProps={{ 'data-testid': 'clientPolicyholderPhone' }}
                      fullWidth
                      // disabled={disablePoliciholderInfoFields}
                      inputHeight={40}
                      value={values['phone']}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handleUpdateFieldValues('phone', e.target.value)
                      }
                      error={touched.phone && Boolean(errors.phone)}
                      helperText={touched.phone && errors.phone && tenDigits}
                    />
                  </Grid>
                </Grid>
                <Grid container p={1} spacing={1} direction="row" alignItems="center">
                  <Grid item xs={4}>
                    <Typography textAlign={'left'}>Street Address 1:</Typography>
                  </Grid>
                  <Grid item xs={7.5}>
                    <FHInputField
                      inputProps={{ 'data-testid': 'clientPolicyholderStreetAddress1' }}
                      inputHeight={40}
                      fullWidth
                      // disabled={disablePoliciholderInfoFields}
                      value={values['street_address_line_one']}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handleUpdateFieldValues('street_address_line_one', e.target.value)
                      }
                      error={
                        touched.street_address_line_one && Boolean(errors.street_address_line_one)
                      }
                      helperText={
                        touched.street_address_line_one &&
                        errors.street_address_line_one &&
                        required
                      }
                    />
                  </Grid>
                </Grid>
                <Grid container p={1} spacing={1} direction="row" alignItems="center">
                  <Grid item xs={4}>
                    <Typography textAlign={'left'}>Street Address 2:</Typography>
                  </Grid>
                  <Grid item xs={7.5}>
                    <FHInputField
                      inputProps={{ 'data-testid': 'clientPolicyholderStreetAddress2' }}
                      inputHeight={40}
                      fullWidth
                      // disabled={disablePoliciholderInfoFields}
                      value={values['street_address_line_two']}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handleUpdateFieldValues('street_address_line_two', e.target.value)
                      }
                    />
                  </Grid>
                </Grid>
                <Grid container p={1} spacing={1} direction="row" alignItems="center">
                  <Grid item xs={4}>
                    <Typography textAlign={'left'}>City:</Typography>
                  </Grid>
                  <Grid item xs={7.5}>
                    <FHInputField
                      inputProps={{ 'data-testid': 'clientPolicyholderCity' }}
                      inputHeight={40}
                      fullWidth
                      // disabled={disablePoliciholderInfoFields}
                      value={values['street_address_city']}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handleUpdateFieldValues('street_address_city', e.target.value)
                      }
                      error={touched.street_address_city && Boolean(errors.street_address_city)}
                      helperText={
                        touched.street_address_city && errors.street_address_city && required
                      }
                    />
                  </Grid>
                </Grid>
                <Grid container p={1} spacing={1} direction="row" alignItems="center">
                  <Grid item xs={4}>
                    <Typography textAlign={'left'}>State:</Typography>
                  </Grid>
                  <Grid item xs={7.5}>
                    <FHInputField
                      inputProps={{ 'data-testid': 'clientPolicyholderState' }}
                      inputHeight={40}
                      fullWidth
                      // disabled={disablePoliciholderInfoFields}
                      value={values['street_address_state']}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handleUpdateFieldValues('street_address_state', e.target.value)
                      }
                      error={touched.street_address_state && Boolean(errors.street_address_state)}
                      helperText={
                        touched.street_address_state && errors.street_address_state && twoChars
                      }
                    />
                  </Grid>
                </Grid>
                <Grid container p={1} spacing={1} direction="row" alignItems="center">
                  <Grid item xs={4}>
                    <Typography textAlign={'left'}>Zip:</Typography>
                  </Grid>
                  <Grid item xs={7.5}>
                    <FHInputField
                      inputProps={{ 'data-testid': 'clientPolicyholderZip' }}
                      fullWidth
                      inputHeight={40}
                      value={values['street_address_zip']}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handleUpdateFieldValues('street_address_zip', e.target.value)
                      }
                      error={touched.street_address_zip && Boolean(errors.street_address_zip)}
                      helperText={
                        touched.street_address_zip && errors.street_address_zip && minFiveDigits
                      }
                    />
                  </Grid>
                </Grid>
                <Grid container p={1} spacing={4} direction="row" alignItems="center">
                  <Grid item xs={4}>
                    <Typography textAlign={'left'}>Gender:</Typography>
                  </Grid>
                  <Grid item xs={7.5}>
                    <FHRadioGroup
                      data-testid="clientPolicyholderGender"
                      row
                      value={values['sex']}
                      sx={{
                        justifyContent: 'flex-start',
                        '& .MuiFormControlLabel-root': {
                          marginRight: 4,
                        },
                      }}
                      name="gender-radio-buttons-group"
                      onChange={(e: SelectChangeEvent<unknown>) =>
                        handleUpdateFieldValues('sex', e.target.value)
                      }
                    >
                      <FormControlLabel
                        value={'male'}
                        data-testid="clientPolicyholderGenderMale"
                        control={<Radio />}
                        label="Male"
                        // disabled={disablePoliciholderInfoFields}
                      />
                      <FormControlLabel
                        value={'female'}
                        data-testid="clientPolicyholderGenderFemale"
                        control={<Radio />}
                        label="Female"
                        // disabled={disablePoliciholderInfoFields}
                      />
                    </FHRadioGroup>
                    {touched.sex && errors.sex && (
                      <FormHelperText sx={{ color: 'red', fontSize: '0.75rem', textAlign: 'left' }}>
                        {required}
                      </FormHelperText>
                    )}
                  </Grid>
                </Grid>

                {!policyId && (
                  <Grid container p={1} spacing={4} direction="row" alignItems="center">
                    <Grid item xs={4}>
                      <Typography textAlign={'left'}>Create EB tracker entry:</Typography>
                    </Grid>
                    <Grid item xs={7.5}>
                      <FHRadioGroup
                        row
                        value={values['create_eb_tracker_entry']}
                        sx={{
                          justifyContent: 'flex-start',
                          '& .MuiFormControlLabel-root': {
                            marginRight: 4,
                          },
                        }}
                        name="create-eb-tracker-entry-radio-group"
                        onChange={(e: SelectChangeEvent<unknown>) =>
                          handleUpdateFieldValues('create_eb_tracker_entry', e.target.value)
                        }
                      >
                        <FormControlLabel value={'yes'} control={<Radio />} label="Yes" />
                        <FormControlLabel value={'no'} control={<Radio />} label="No" />
                      </FHRadioGroup>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Box m={2} sx={{ display: 'flex' }}>
              <FHButton data-testid="clientPolicySubmit" onClick={submitForm}>
                Submit
              </FHButton>
            </Box>
          </form>
        </FBoxContainer>
      </Box>
    </PageContainer>
  );
}

export default ClientPolicyForm;
